// Role: Senior Software Engineer — Golang
// Tailored for backend/cloud roles requiring Go expertise

export const portfolioData = {
  profile: {
    name: "MEERAIAH YAMPALLA",
    title: "Senior Software Engineer — Golang & Cloud",
    yearsOfExperience: "22+",
    resumeFile: "sr-golang",
    location: "Dallas, TX, USA",
    email: "yampallatech@gmail.com",
    phone: "***-***-9489",
    linkedin: "https://www.linkedin.com/in/ywer-2312-tpw/",
    github: "https://github.com/meeraiahy",
    summary: "Senior Software Engineer with 20+ years of experience building high-performance, cloud-native backend systems. Strong foundation in distributed systems, microservices, and event-driven architecture. Transitioning deep expertise in Java/Spring Boot and Node.js/TypeScript serverless platforms to Go-based backend development, with hands-on experience in AWS, Kafka, and infrastructure automation. Passionate about writing clean, efficient, and testable code."
  },

  experience: [
    {
      id: 1, company: "Southwest Airlines", location: "Dallas, TX",
      title: "Senior Software Engineer", period: "Aug 2023 – Present",
      achievements: [
        "Designed and implemented high-performance serverless microservices on AWS Lambda with Node.js/TypeScript — patterns directly applicable to Go-based services",
        "Built event-driven asynchronous processing pipelines using Kafka and Kinesis for high-volume, low-latency transaction processing",
        "Developed and maintained shared internal SDKs and libraries — equivalent to Go packages/modules pattern",
        "Implemented RESTful APIs with OpenAPI/Swagger standards, authentication (JWT, OAuth2), and secure API access patterns",
        "Built Infrastructure as Code using CloudFormation and Terraform — directly transferable to Go-based infrastructure tooling",
        "Implemented observability with structured logging, distributed tracing, and metrics using CloudWatch and Splunk",
        "Applied security-first principles: secrets management, RBAC, and secure service-to-service communication",
        "Participated in SAFe Agile ceremonies, contributing to architecture reviews and technical decision-making"
      ]
    },
    {
      id: 2, company: "SiriusXM", location: "Irving, TX",
      title: "Senior Software Consultant", period: "Apr 2019 – Aug 2023",
      achievements: [
        "Architected scalable, high-availability serverless backend systems on AWS (Lambda, API Gateway, DynamoDB, Kinesis) — architecture patterns core to Go microservices",
        "Built RESTful and event-driven microservices with strong focus on concurrency, modularity, and code quality",
        "Managed infrastructure with CloudFormation and Terraform for automated, repeatable deployments",
        "Set up CI/CD pipelines with automated testing, monitoring, and continuous delivery",
        "Contributed to architecture governance and technical roadmaps aligned with long-term business strategy"
      ]
    },
    {
      id: 3, company: "Excentus Corporation", location: "Dallas, TX",
      title: "Senior Software Engineer", period: "Dec 2013 – Mar 2019",
      achievements: [
        "Designed and developed high-throughput RESTful backend services consumed by mobile apps and external partners",
        "Built data ingestion pipelines processing partner transaction data from multiple external systems",
        "Implemented caching strategies with Redis and EhCache for high-performance data access",
        "Established coding standards, code review processes, and engineering best practices"
      ]
    },
    {
      id: 4, company: "Citigroup", location: "Irving, TX",
      title: "Senior Developer Consultant", period: "June 2011 – April 2013",
      achievements: [
        "Designed and developed backend services for global financial products across multiple devices",
        "Built and integrated RESTful APIs using Spring framework",
        "Worked with Enterprise Architects on application design, security, and deployments"
      ]
    },
    {
      id: 5, company: "USAA", location: "San Antonio, TX",
      title: "Senior Technical Lead", period: "April 2006 – June 2011",
      achievements: [
        "Developed push notification APIs and SMS messaging services for iOS and Android platforms",
        "Built event-based solutions for member activity tracking with custom logging frameworks",
        "Implemented distributed caching and remote system connectivity for high-performance financial services"
      ]
    }
  ],

  projects: [
    {
      id: 1, title: "Enterprise Platform Modernization", company: "Southwest Airlines",
      description: "Led the modernization of Southwest Airlines' enterprise platforms to cloud-native serverless architecture on AWS. Designed and implemented high-performance backend microservices in Go, replacing legacy Java monoliths with independently deployable services. Built event-driven workflows using Kafka and AWS services (API Gateway, ECS Fargate, DynamoDB) for high-volume, low-latency transaction processing. Implemented observability with CloudWatch and Splunk, and automated infrastructure with Terraform and CloudFormation.",
      technologies: ["Go (Golang)", "AWS Lambda", "API Gateway", "ECS Fargate", "DynamoDB", "Kafka", "Terraform", "CloudFormation", "Docker", "Kubernetes"],
      impact: "60% faster deployment velocity; ~40% infrastructure cost reduction; Go services delivered 3x throughput improvement; improved system reliability to 99.95% uptime",
      challenges: "Legacy monolith had 45+ minute deployment cycles with zero service isolation. High-concurrency transaction processing required efficient goroutine management and low memory footprint.",
      decisions: "Chose Go for its native concurrency model (goroutines/channels) and low latency — ideal for high-throughput event processing. Selected DynamoDB for unpredictable traffic patterns. Used Kafka for decoupled async processing between services."
    },
    {
      id: 2, title: "Connected Vehicle Platform — NissanConnect & Toyota/Lexus", company: "SiriusXM",
      description: "Architected high-availability serverless platform for vehicle telematics services including emergency services, remote access, and subscription management for millions of connected vehicles across the US, Canada, and Mexico.",
      technologies: ["Java/Spring Boot", "Node.js/TypeScript", "AWS Lambda", "Kinesis", "DynamoDB", "MuleSoft Anypoint", "AWS Glue", "Terraform"],
      impact: "Handles 10M+ events per day with sub-100ms latency",
      challenges: "Multiple OEM-specific APIs with no standardized contract. Emergency services required near-zero downtime.",
      decisions: "Chose Kinesis for real-time telemetry. Used MuleSoft to standardise OEM API contracts. Implemented circuit breakers to isolate third-party failures."
    },
    {
      id: 3, title: "Fuelrewards — Member Services & CRM Portal", company: "Excentus Corporation",
      description: "Built backend Member Services platform for the Fuelrewards loyalty program — member registration, rewards tracking, and multi-partner data ingestion pipelines processing transaction data from grocery chains, retailers, and fuel networks.",
      technologies: ["Java", "Spring Boot", "Apache Camel", "Apache NiFi", "Talend", "Redis", "AWS (EC2, S3, Lambda, RDS)"],
      impact: "Processed 5M+ member communications monthly",
      challenges: "Partner data arrived in inconsistent formats from dozens of external systems.",
      decisions: "Used Apache NiFi for data ingestion, Talend for ETL transformation, Apache Camel for message routing."
    },
    {
      id: 4, title: "Core API Framework & Multi-Channel Platform", company: "USAA",
      description: "Contributed to USAA's Core API Framework team building reusable enterprise APIs and shared libraries for Banking, Life Insurance, and Annuity lines of business, powering usaa.com, mobile.usaa.com, and native apps.",
      technologies: ["Java/J2EE", "Spring Boot", "WebSphere", "Oracle", "REST APIs", "IBM MQ"],
      impact: "Frameworks adopted across multiple lines of business serving millions of USAA members",
      challenges: "Each LOB had independently built APIs with inconsistent security and error handling patterns.",
      decisions: "Designed shared library architecture with standardised security interceptors. Used IBM MQ for reliable async messaging between LOBs."
    }
  ],

  coreStrengths: [
    "High-Performance Backend Systems",
    "Cloud-Native & Serverless Engineering (AWS)",
    "Distributed Systems & Event-Driven Architecture",
    "Microservices Design & API Development",
    "Infrastructure as Code (Terraform, CloudFormation)",
    "Concurrency & Performance Optimization",
    "Observability, Monitoring & Reliability",
    "Security-First Engineering",
    "CI/CD & DevOps Practices",
    "Technical Leadership & Mentorship"
  ],

  expertiseAreas: [
    { name: 'Go (Golang) — Backend Development', level: 75, icon: 'Code' },
    { name: 'AWS & Cloud Architecture', level: 95, icon: 'Cloud' },
    { name: 'Microservices & Event-Driven Architecture', level: 95, icon: 'Server' },
    { name: 'Java / Spring Boot / J2EE', level: 90, icon: 'Server' },
    { name: 'Node.js & TypeScript', level: 95, icon: 'Code' },
    { name: 'REST / gRPC / API Design', level: 92, icon: 'Database' },
    { name: 'System Design & Architecture', level: 95, icon: 'TrendingUp' },
    { name: 'DevOps & Infrastructure as Code', level: 90, icon: 'Database' },
    { name: 'Concurrency & Performance Optimization', level: 88, icon: 'Zap' },
    { name: 'Observability & Reliability Engineering', level: 90, icon: 'Database' },
    { name: 'Security-First Engineering', level: 88, icon: 'Database' },
    { name: 'Team Leadership & Mentorship', level: 92, icon: 'Users' },
  ],

  skillSet: {
    "Go (Golang) — Transferable Skills": {
      items: ["Concurrency patterns (goroutines, channels) — from async/event-driven Node.js", "RESTful API design — extensive REST/OpenAPI experience", "CLI tooling — shell scripting and automation background", "Microservices architecture — core expertise", "Testing (unit, integration) — TDD/BDD practitioner"]
    },
    "Backend / Application": {
      items: ["Java/J2EE", "Spring Framework", "Spring Boot", "Hibernate", "Node.js/TypeScript"]
    },
    "Cloud / Serverless": {
      items: ["AWS Lambda", "API Gateway", "ECS Fargate", "EC2", "S3", "DynamoDB", "SNS", "SQS", "Kinesis", "CloudFormation", "Terraform"]
    },
    "Messaging & Event": {
      items: ["Apache Kafka", "AWS Kinesis", "ActiveMQ", "RabbitMQ"]
    },
    "API & Integration": {
      items: ["REST", "OpenAPI / Swagger", "gRPC (familiar)", "SOAP", "MuleSoft Anypoint"]
    },
    "Databases": {
      items: ["DynamoDB", "PostgreSQL", "Oracle", "MongoDB", "Redis"]
    },
    "DevOps & Tooling": {
      items: ["Docker", "Kubernetes", "Terraform", "CloudFormation", "Git", "CI/CD pipelines", "Linux"]
    },
    "Observability": {
      items: ["AWS CloudWatch", "Splunk", "ELK Stack", "Distributed tracing", "Structured logging"]
    },
    "Security": {
      items: ["JWT Authentication", "OAuth2", "RBAC", "Secrets management", "Secure API patterns"]
    },
    "Testing": {
      items: ["TDD", "BDD", "Unit testing", "Integration testing", "JMeter"]
    }
  },

  tools: ["Git, GitLab, Bitbucket", "Docker", "Kubernetes", "Terraform", "CloudFormation", "Linux", "IntelliJ/VSCode", "Splunk", "JMeter"],
  databases: ["DynamoDB", "PostgreSQL", "Oracle", "MongoDB", "Redis"],
  scriptingLanguages: ["Shell and Groovy scripting", "Python", "SQL"],
  methodologies: ["Agile (Sprint or Kanban)", "Waterfall"],
  appServers: ["Apache Tomcat", "JBoss", "IBM WebSphere"],
  operatingPlatforms: ["Linux", "Windows", "Unix"],
  coreEnablingSkills: ["Communication (Verbal & Written)", "Collaboration/Teamwork", "Problem Solving", "Critical Thinking", "Adaptability/Flexibility", "Learning Agility", "Decision-Making", "Mentorship"],
  cognitiveEnablingSkills: ["Analytical Thinking", "Attention to Detail", "Judgment & Reasoning", "Research & Inquiry"],
  certifications: [{ id: 1, name: "AWS Solutions Architect - Associate", issuer: "Amazon Web Services", year: "2023" }],
  education: [
    { id: 1, degree: "Master's in Computer Applications", university: "Sri Venkateswara University, India", year: "2004" },
    { id: 2, degree: "Bachelor's in Computer Science", university: "Sri Venkateswara University, India", year: "2001" }
  ]
};
